import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  DashboardWidget,
  StatMetric,
  VenueMapMarker,
} from '../../../../../models/big-screen-dashboard.model';
import { EntranceGroup } from '../../entrance-groups/entrance-groups.component';
import {
  applyEntranceGroups,
  computeEntranceRates,
  computeErrorAggregates,
  entranceNames as summaryEntranceNames,
  formatErrorCode,
} from '../../../../../services/attendance-metrics';
import { downscaleImageToDataUrl, ImageLoadError } from '../../../../../services/image-utils';

/** One selectable stat metric with its display label. */
interface StatOption {
  value: StatMetric;
  label: string;
}

/** Default font size (px) for a text note when none has been set yet. */
const DEFAULT_TEXT_FONT_SIZE = 32;

/** Word-style font sizes offered in the text note's size dropdown. */
const TEXT_FONT_SIZES: number[] = [
  8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96, 128,
];

/**
 * Settings modal for a single widget. Edits a working copy so Cancel discards, and
 * emits the updated widget on Save. Which controls show depends on the widget type:
 * a stat picker, an entrance add/remove list (+ grouping toggle and a reused
 * "Manage groups" modal), an error-code add/remove list, or a graph source +
 * entrance selection. "All data points" is represented as an unset selection.
 */
@Component({
  selector: 'app-widget-config',
  standalone: false,
  templateUrl: './widget-config.component.html',
  // shared.scss first so this component's own rules (e.g. the .modal z-index
  // override that lifts the config above the big-screen overlay) win the cascade.
  styleUrls: ['../../../styles/shared.scss', './widget-config.component.scss'],
})
export class WidgetConfigComponent implements OnChanges {
  @Input() widget: DashboardWidget | null = null;
  /**
   * True while configuring a freshly-added widget. Only new fan-out widgets (stat
   * tile, single-line graph) allow multi-select — each pick spawns its own widget.
   * Editing an existing one is single-select, so the other options are disabled.
   */
  @Input() isNew: boolean = false;
  @Input() attendanceSummary: any = {};
  @Input() entranceGroups: EntranceGroup[] = [];
  @Input() timedGateEntries: { gate: string }[] = [];
  @Input() timedScannerEntries: { gate: string }[] = [];
  @Input() dark: boolean = false;

  @Output() save = new EventEmitter<DashboardWidget>();
  @Output() cancel = new EventEmitter<void>();
  /** Raised when the user edits entrance groups so the parent can persist them. */
  @Output() groupsChange = new EventEmitter<EntranceGroup[]>();

  /** Working copy; committed only on Save. */
  draft: DashboardWidget | null = null;
  /** Selection set for the currently-edited widget (entrance names or codes). */
  selected: Set<string> = new Set<string>();
  entranceGroupsModalOpen: boolean = false;
  /** True while a venue-map image is being read and downscaled. */
  imageBusy: boolean = false;
  /** Set when an uploaded venue-map image can't be read, shown inline. */
  imageError: string = '';

  /** Word-style font-size presets for the text note's size control. */
  readonly fontSizes: number[] = TEXT_FONT_SIZES;

  readonly statOptions: StatOption[] = [
    { value: 'entries', label: 'Entries' },
    { value: 'inVenue', label: 'Fans Inside' },
    { value: 'exits', label: 'Exits' },
    { value: 'reentries', label: 'Reentries' },
    { value: 'internal', label: 'Internal' },
    { value: 'totalScanRate', label: 'Total Scan Rate' },
    { value: 'successRate', label: 'Successful Scans' },
    { value: 'errorRate', label: 'Error Scans' },
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widget']) {
      this.imageError = '';
      this.imageBusy = false;
      if (this.widget) {
        this.draft = { ...this.widget, layout: { ...this.widget.layout } };
        this.seedSelection();
      } else {
        // Parent cleared the selection (Save/Cancel/close) — drop the working copy
        // so *ngIf="draft" hides the modal.
        this.draft = null;
      }
    }
  }

  /**
   * Seeds the literal checkbox selection from the widget's saved list. For the
   * "show all when unset" widgets an unset list means every item is checked (so the
   * user starts from all and unchecks what they don't want); an explicit stored
   * list — including an empty one — is taken literally.
   */
  private seedSelection(): void {
    if (!this.draft) return;
    if (this.draft.type === 'textNote') {
      // Default older/unset notes so the font-size control has a value to bind.
      if (this.draft.fontSize === undefined) this.draft.fontSize = DEFAULT_TEXT_FONT_SIZE;
      return;
    }
    if (this.draft.type === 'statTile') {
      // A stat tile's selection is the checked metrics; seed it from the tile's
      // current metric so re-opening the config shows it ticked.
      this.selected = new Set(this.draft.statMetric ? [this.draft.statMetric] : []);
      return;
    }
    if (this.draft.type === 'venueMap') {
      // A venue map's selection is the set of entrances that have a marker.
      this.selected = new Set((this.draft.markers ?? []).map((m) => m.entranceName));
      return;
    }
    const list =
      this.draft.type === 'errorsByResultCode' ? this.draft.errorCodes : this.draft.entranceNames;
    if (list) {
      this.selected = new Set(list);
    } else if (this.selectsAllByDefault) {
      this.selected = new Set(this.currentUniverse());
    } else {
      this.selected = new Set();
    }
  }

  /** All entrance names available for this venue (respecting grouping for scan-rate). */
  get availableEntrances(): string[] {
    if (!this.draft) return [];
    if (this.draft.type === 'scanRateByEntrance' && this.draft.useGrouping) {
      const rates = computeEntranceRates(this.attendanceSummary);
      return applyEntranceGroups(rates, this.entranceGroups, true).map((t) => t.name);
    }
    if (this.draft.type === 'multiLineGraph' || this.draft.type === 'entranceGraph') {
      const source =
        this.draft.graphSource === 'scanner' ? this.timedScannerEntries : this.timedGateEntries;
      return source.map((e) => e.gate);
    }
    return summaryEntranceNames(this.attendanceSummary);
  }

  /** All result codes available for this venue. */
  get availableCodes(): string[] {
    return computeErrorAggregates(this.attendanceSummary).map((e) => e.code);
  }

  /** Raw (ungrouped) entrance names for the Manage Groups modal. */
  get rawEntranceNames(): string[] {
    return summaryEntranceNames(this.attendanceSummary);
  }

  formatErrorCode(code: string): string {
    return formatErrorCode(code);
  }

  /**
   * Whether the current widget treats an *unset* selection as "show all". These
   * widgets (scan-rate, errors, multi-line graph) render every data point when no
   * list is stored, so opening the config seeds them with the full universe checked;
   * the others (stat tile, venue map, single-entrance graph) are literal — an empty
   * selection means nothing is chosen.
   */
  private get selectsAllByDefault(): boolean {
    return (
      this.draft?.type === 'scanRateByEntrance' ||
      this.draft?.type === 'errorsByResultCode' ||
      this.draft?.type === 'multiLineGraph'
    );
  }

  /**
   * The selection is now always literal: each checkbox stands only for itself, so a
   * box is ticked exactly when its key is in the set. "Show all" is materialised as
   * every key being checked rather than an empty set, which lets "unselect all"
   * genuinely show nothing.
   */
  isSelected(key: string): boolean {
    return this.selected.has(key);
  }

  /**
   * Whether the current widget is limited to a single choice. Stat tiles and
   * single-line graphs fan a multi-selection out into one widget each, which only
   * makes sense when adding a new widget. When editing an existing one, extra picks
   * would silently spawn *new* widgets rather than edit the open one, so the
   * selection is capped at one.
   */
  get isSingleSelect(): boolean {
    return !this.isNew && (this.draft?.type === 'statTile' || this.draft?.type === 'entranceGraph');
  }

  /**
   * Whether the shared title controls (heading, size, colour, bold) should be shown.
   * A fan-out widget (stat tile, single-line graph) with more than one option checked
   * will split into several widgets on Save, so a single custom heading/styling would
   * be meaningless — hide the controls in that case.
   */
  get showTitleControls(): boolean {
    const fansOut = this.draft?.type === 'statTile' || this.draft?.type === 'entranceGraph';
    return !(fansOut && this.selected.size > 1);
  }

  /**
   * Whether a checkbox should be disabled: only in single-select mode, and only for
   * the options that aren't the current pick, so the user must uncheck the current
   * choice before selecting another.
   */
  isDisabled(key: string): boolean {
    return this.isSingleSelect && this.selected.size > 0 && !this.selected.has(key);
  }

  toggle(key: string): void {
    if (this.selected.has(key)) {
      this.selected.delete(key);
    } else {
      // In single-select mode, ignore clicks on the disabled (non-current) options.
      if (this.isDisabled(key)) return;
      this.selected.add(key);
    }
  }

  /** Whether every selectable key is currently checked. */
  get allSelected(): boolean {
    const universe = this.currentUniverse();
    return universe.length > 0 && universe.every((key) => this.selected.has(key));
  }

  /** Checks every selectable key. */
  selectAll(): void {
    this.selected = new Set(this.currentUniverse());
  }

  /** Clears the selection so nothing is checked. */
  unselectAll(): void {
    this.selected = new Set();
  }

  /** Header control: flip between all-checked and none-checked in one click. */
  toggleSelectAll(): void {
    if (this.allSelected) {
      this.unselectAll();
    } else {
      this.selectAll();
    }
  }

  /** The full set of keys the current widget can select from. */
  private currentUniverse(): string[] {
    if (this.draft?.type === 'statTile') {
      return this.statOptions.map((opt) => opt.value);
    }
    return this.draft?.type === 'errorsByResultCode' ? this.availableCodes : this.availableEntrances;
  }

  onGroupingToggle(): void {
    if (!this.draft) return;
    this.draft.useGrouping = !this.draft.useGrouping;
    // Grouping changes the tile universe; re-seed to all checked so the fresh
    // universe starts from "show all" (matching how an unset list renders).
    this.selected = new Set(this.currentUniverse());
  }

  onGraphSourceChange(source: 'gate' | 'scanner'): void {
    if (!this.draft) return;
    this.draft.graphSource = source;
    // A multi-line graph shows all by default, so re-seed its new source's
    // entrances as checked; the literal single-entrance graph starts empty.
    this.selected = this.selectsAllByDefault ? new Set(this.currentUniverse()) : new Set();
  }

  /**
   * Reads the chosen floor-plan file, downscales it for storage, and stores it on
   * the draft as a data URL. Runs off the file input's change event; a failed read
   * surfaces inline rather than throwing.
   */
  async onImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset the input so re-selecting the same file still fires a change event.
    input.value = '';
    if (!file || !this.draft) return;

    this.imageError = '';
    this.imageBusy = true;
    try {
      this.draft.imageData = await downscaleImageToDataUrl(file);
    } catch (error) {
      this.imageError =
        error instanceof ImageLoadError ? error.message : 'Could not load that image.';
    } finally {
      this.imageBusy = false;
    }
  }

  /** Clears the venue-map image from the draft (markers are kept). */
  removeImage(): void {
    if (this.draft) this.draft.imageData = undefined;
  }

  /** Sets the text note's custom colour from the colour picker. */
  onTextColorChange(event: Event): void {
    if (!this.draft) return;
    this.draft.textColor = (event.target as HTMLInputElement).value;
  }

  /** Sets the widget title's custom colour from the colour picker. */
  onTitleColorChange(event: Event): void {
    if (!this.draft) return;
    this.draft.titleColor = (event.target as HTMLInputElement).value;
  }

  openEntranceGroups(): void {
    this.entranceGroupsModalOpen = true;
  }

  closeEntranceGroups(): void {
    this.entranceGroupsModalOpen = false;
  }

  onEntranceGroupsSaved(groups: EntranceGroup[]): void {
    this.entranceGroupsModalOpen = false;
    this.groupsChange.emit(groups);
  }

  onSave(): void {
    if (!this.draft) return;
    const universe = this.currentUniverse();
    const picked = universe.filter((k) => this.selected.has(k));
    // For "show all by default" widgets, storing every item as an explicit list is
    // equivalent to "all" — but store "all" (unset) so newly-appearing data points
    // are included automatically. A partial pick (including an empty one, meaning
    // "show nothing") is stored literally so unselect-all is honoured.
    const selectedList = picked.length >= universe.length ? undefined : picked;

    if (this.draft.type === 'statTile') {
      // A stat tile shows one metric. Its selection is literal, so emit the checked
      // metrics as statMetrics and let the dashboard fan them out into one tile
      // each. An empty pick falls back to the current metric (or the first option)
      // so the tile still shows a figure.
      const metrics = picked as StatMetric[];
      this.draft.statMetrics =
        metrics.length > 0
          ? metrics
          : [this.draft.statMetric ?? (universe[0] as StatMetric)];
    } else if (this.draft.type === 'image') {
      // A standalone image carries only its uploaded imageData; nothing to persist
      // from the selection controls.
    } else if (this.draft.type === 'venueMap') {
      this.draft.markers = this.reconcileMarkers();
    } else if (this.draft.type === 'errorsByResultCode') {
      this.draft.errorCodes = selectedList;
    } else if (this.draft.type === 'entranceGraph') {
      // A single-entrance graph shows exactly one line. Its selection is literal,
      // so emit exactly the checked entrances and let the dashboard fan them out
      // into one widget per line. An empty pick falls back to the first entrance
      // so the widget still renders a line rather than nothing.
      this.draft.entranceNames = picked.length > 0 ? picked : universe.slice(0, 1);
    } else if (this.draft.type !== 'textNote') {
      this.draft.entranceNames = selectedList;
    }
    this.save.emit(this.draft);
  }

  /**
   * Builds the venue-map marker list from the checked entrances: a still-checked
   * entrance keeps its existing position, and a newly-checked one gets a marker
   * near the top-left so it's visible and draggable. Unchecked entrances drop out.
   */
  private reconcileMarkers(): VenueMapMarker[] {
    const existing = new Map(
      (this.draft?.markers ?? []).map((marker) => [marker.entranceName, marker])
    );
    return this.availableEntrances
      .filter((name) => this.selected.has(name))
      .map((name) => existing.get(name) ?? { entranceName: name, x: 10, y: 10 });
  }

  onCancel(): void {
    this.cancel.emit();
  }
}
